import { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { LinkContainer } from 'react-router-bootstrap';
import { toast } from 'react-toastify';
import {
  FaBolt,
  FaCampground,
  FaChartLine,
  FaClipboardList,
  FaFlag,
  FaRoute,
  FaSatelliteDish,
  FaStopwatch,
  FaUsers,
} from 'react-icons/fa';
import { useFetchAthleteByUserIdQuery } from '../slices/athleteApiSlice';
import { useGetGarminAuthUrlMutation, useGetGarminStatusQuery } from '../slices/garminApiSlice';
import { useGetStravaAuthUrlMutation } from '../slices/stravaApiSlice';
import { useFetchLeaguesQuery } from '../slices/leaguesApiSlice';
import { useGetDraftByLeagueQuery } from '../slices/draftApiSlice';
import { useGetTeamByIdQuery } from '../slices/teamApiSlice';
import { useGetCurrentSeasonQuery } from '../slices/seasonsApiSlice';
import { buildDraftRoomPath } from '../utils/draftRoomMode';
import { getLeaguePath, useActiveLeagueId } from '../utils/activeLeague';

const getCurrentWeekNumber = (season) => {
  if (!season?.startDate) return null;

  const startDate = new Date(season.startDate);
  const now = new Date();

  if (Number.isNaN(startDate.getTime())) return null;
  if (now < startDate) return null;

  const diffMs = now.getTime() - startDate.getTime();
  return Math.floor(diffMs / (1000 * 60 * 60 * 24 * 7)) + 1;
};

const ACTIVE_SCORERS_PER_TEAM = 5;

const normalizeBenchAthleteIds = (value) => {
  if (!value) return [];
  const values = Array.isArray(value) ? value : [value];
  const seenIds = new Set();

  return values.reduce((ids, entry) => {
    const normalizedId = String(entry?._id || entry || '').trim();
    if (!normalizedId || seenIds.has(normalizedId)) {
      return ids;
    }

    seenIds.add(normalizedId);
    ids.push(normalizedId);
    return ids;
  }, []);
};

const getBenchSlotsForRosterSize = (rosterSize) => Math.max(0, Number(rosterSize || 0) - ACTIVE_SCORERS_PER_TEAM);

const getConfigurableBenchWeekNumber = (season) => {
  if (!season?.startDate) return null;

  const startDate = new Date(season.startDate);
  const now = new Date();

  if (Number.isNaN(startDate.getTime())) return null;
  if (now < startDate) return 1;

  const currentWeekNumber = getCurrentWeekNumber(season);
  return currentWeekNumber == null ? null : currentWeekNumber + 1;
};

const getBenchAthleteIdsForWeek = ({ team, rosterSize, weekNumber }) => {
  const weeklyBenchSelections = normalizeWeeklyBenchSelections(team?.weeklyBenchSelections);
  const weeklySelection = weekNumber ? normalizeBenchAthleteIds(weeklyBenchSelections[String(weekNumber)]) : [];

  if (weeklySelection.length > 0) {
    return weeklySelection;
  }

  const defaultBenchAthleteIds = normalizeBenchAthleteIds(team?.benchAthletes);
  if (defaultBenchAthleteIds.length > 0) {
    return defaultBenchAthleteIds;
  }

  return normalizeBenchAthleteIds(team?.benchAthlete).slice(0, getBenchSlotsForRosterSize(rosterSize));
};

const normalizeWeeklyBenchSelections = (weeklyBenchSelections) => {
  if (!weeklyBenchSelections) return {};
  if (weeklyBenchSelections instanceof Map) {
    return Object.fromEntries(weeklyBenchSelections);
  }
  if (Array.isArray(weeklyBenchSelections)) {
    return Object.fromEntries(weeklyBenchSelections);
  }
  return weeklyBenchSelections;
};

const formatDateTime = (dateValue) => {
  if (!dateValue) return null;

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const isSameLocalDate = (a, b) =>
  a?.getFullYear?.() === b?.getFullYear?.() &&
  a?.getMonth?.() === b?.getMonth?.() &&
  a?.getDate?.() === b?.getDate?.();

const stats = [
  { icon: <FaRoute />, value: 'Live', label: 'Your miles = Points' },
  { icon: <FaFlag />, value: 'Social', label: 'Live Draft' },
  { icon: <FaSatelliteDish />, value: 'Personal', label: 'Your Friends are Your Team' },
  { icon: <FaUsers />, value: 'Head-to-head', label: 'Weekly matchups' },
];

const featureHighlights = [
  { icon: <FaUsers />, label: 'Build teams with your league' },
  { icon: <FaRoute />, label: 'Track mileage and points' },
  { icon: <FaChartLine />, label: 'See progress week by week' },
  { icon: <FaCampground />, label: 'Stay connected all season' },
];

const LEAGUE_MANAGER_SLIDESHOW_PATH = '/league-manager-slideshow';
const LEAGUE_MANAGER_SLIDESHOW_THUMBNAIL = '/league-manager-slideshow/assets/slide-01.png';
const LEAGUE_MANAGER_SLIDESHOW_STORAGE_KEY = 'lr-league-manager-slideshow-dismissed';

const HomeScreen = () => {
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);
  const [showLeagueManagerIntro, setShowLeagueManagerIntro] = useState(false);
  const storedActiveLeagueId = useActiveLeagueId();
  const athleteRequest = storedActiveLeagueId
    ? { userId: userInfo?._id, leagueId: storedActiveLeagueId }
    : userInfo?._id;
  const { data: athlete } = useFetchAthleteByUserIdQuery(athleteRequest, {
    skip: !userInfo,
  });
  const [getGarminAuthUrl] = useGetGarminAuthUrlMutation();
  const [getStravaAuthUrl] = useGetStravaAuthUrlMutation();
  const { data: garminStatus } = useGetGarminStatusQuery(undefined, {
    skip: !userInfo,
  });
  const leagueId = athlete?.league?._id || athlete?.league;
  const teamId = athlete?.team?._id || athlete?.team;
  const { data: userLeague } = useFetchLeaguesQuery(leagueId, {
    skip: !leagueId,
  });
  const { data: existingDraft } = useGetDraftByLeagueQuery(leagueId, {
    skip: !leagueId,
  });

  const handleStravaAuth = async () => {
    try {
      const { authUrl } = await getStravaAuthUrl().unwrap();
      window.location.assign(authUrl);
    } catch (error) {
      console.error('Failed to start Strava auth', error);
      toast.error(error?.data?.message || 'Failed to start Strava connection');
    }
  };
  const { data: team } = useGetTeamByIdQuery(teamId, {
    skip: !teamId,
  });
  const { data: currentSeason } = useGetCurrentSeasonQuery(leagueId, {
    skip: !leagueId,
  });
  const leaguePath = getLeaguePath(leagueId || storedActiveLeagueId);

  const currentWeekNumber = getCurrentWeekNumber(currentSeason?.season);
  const configurableBenchWeekNumber = getConfigurableBenchWeekNumber(currentSeason?.season);
  const weeklyBenchSelections = normalizeWeeklyBenchSelections(team?.weeklyBenchSelections);
  const rosterSize = Array.isArray(team?.athlete) ? team.athlete.length : 0;
  const requiredBenchSlots = getBenchSlotsForRosterSize(rosterSize);
  const configurableBenchAthleteIds = getBenchAthleteIdsForWeek({
    team,
    rosterSize,
    weekNumber: configurableBenchWeekNumber,
  });
  const isLeagueAdmin =
    leagueId &&
    String(userLeague?.admin?._id || userLeague?.admin) === String(athlete?._id);
  const isTeamManager =
    String(team?.admin?._id || team?.admin) === String(userInfo?._id) ||
    String(teamId) === String(athlete?.team?._id || athlete?.team);
  const adminName =
    userLeague?.admin?.user?.username ||
    [userLeague?.admin?.firstName, userLeague?.admin?.lastName].filter(Boolean).join(' ') ||
    'your league manager';
  const draftStartsAt = formatDateTime(existingDraft?.startTime);
  const draftStartDate = existingDraft?.startTime ? new Date(existingDraft.startTime) : null;
  const hasValidDraftStartDate = Boolean(draftStartDate && !Number.isNaN(draftStartDate.getTime()));
  const showHeroEnterDraft =
    Boolean(existingDraft?._id) &&
    hasValidDraftStartDate &&
    isSameLocalDate(draftStartDate, new Date()) &&
    (existingDraft.status === 'scheduled' || existingDraft.status === 'active');
  const showHeroDraftDate = existingDraft?.status === 'scheduled' && draftStartsAt;
  const hasConnectedTracker = Boolean(userInfo?.hasStravaConnection || garminStatus?.connected);
  const showIntegrationButtons = Boolean(userInfo) && !hasConnectedTracker;

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const hasDismissedIntro = window.localStorage.getItem(LEAGUE_MANAGER_SLIDESHOW_STORAGE_KEY) === 'true';
    if (!hasDismissedIntro) {
      setShowLeagueManagerIntro(true);
    }
  }, []);

  const handleGarminAuth = async () => {
    try {
      const { authUrl } = await getGarminAuthUrl().unwrap();
      const popup = window.open(authUrl, 'garmin_oauth', 'width=600,height=700');

      if (!popup || popup.closed || typeof popup.closed === 'undefined') {
        toast.info('Your browser blocked the Garmin popup. We’re opening Garmin in this tab instead.');
        window.location.assign(authUrl);
        return;
      }

      popup.focus?.();
    } catch (error) {
      console.error('Failed to start Garmin auth', error);
      toast.error(error?.data?.message || 'Failed to start Garmin connection');
    }
  };

  const priorityCallouts = [];

  if (userInfo && !leagueId) {
    priorityCallouts.push({
      key: 'join-league',
      icon: <FaUsers />,
      tone: 'warning',
      title: 'Join or create a league',
      description: 'You need a league before draft setup, teams, and scoring can move forward.',
      actionLabel: 'Open Leagues',
      onClick: () => navigate('/leagues'),
    });
  }

  if (leagueId && !teamId) {
    priorityCallouts.push({
      key: 'create-team',
      icon: <FaFlag />,
      tone: 'warning',
        title: 'Create your league',
        description: 'Open league creation so you can start a new league setup from the leagues hub.',
        actionLabel: 'Create Your League',
        onClick: () => navigate('/leagues?intent=create-league'),
    });
  }

  if (leagueId && isLeagueAdmin && !existingDraft) {
    priorityCallouts.push({
      key: 'schedule-draft',
      icon: <FaStopwatch />,
      tone: 'danger',
      title: 'Your league still needs a draft date',
      description: 'Set the draft schedule so managers can prepare rosters and the season can move forward.',
      actionLabel: 'Set Draft Date',
      onClick: () => navigate(`/leagues/${leagueId}/manage`),
    });
  }

  if (leagueId && isLeagueAdmin) {
    priorityCallouts.push({
      key: 'invite-athletes',
      icon: <FaUsers />,
      tone: 'accent',
      title: 'Invite athletes to your league',
      description: 'Open league invitations so you can send invite emails and manage who joins.',
      actionLabel: 'Invite Athletes',
      onClick: () => navigate(`/leagues/${leagueId}/manage`),
    });
  }

  if (leagueId && !isLeagueAdmin && !existingDraft) {
    priorityCallouts.push({
      key: 'waiting-on-manager',
      icon: <FaClipboardList />,
      tone: 'info',
      title: 'Draft setup is still pending',
      description: `${adminName} has not scheduled the draft yet. Once a date is set, your next steps will appear here.`,
      actionLabel: 'View League Hub',
      onClick: () => navigate(leaguePath),
    });
  }

  if (existingDraft?.status === 'scheduled') {
    priorityCallouts.push({
      key: 'draft-scheduled',
      icon: <FaBolt />,
      tone: 'accent',
      title: draftStartsAt ? `Get ready for the draft on ${draftStartsAt}` : 'Get ready for the draft',
      description: isLeagueAdmin
        ? 'Your draft is scheduled. Review the room and make sure managers know when to show up.'
        : 'Your league draft is on the calendar. Make sure your team is ready before the room opens.',
      actionLabel: 'Open Draft Room',
      onClick: () => navigate(buildDraftRoomPath(existingDraft._id, { chooseView: true })),
    });
  }

  if (existingDraft?.status === 'active') {
    priorityCallouts.push({
      key: 'draft-live',
      icon: <FaBolt />,
      tone: 'success',
      title: 'The draft is live now',
      description: teamId
        ? 'Jump into the draft room to make picks and keep your roster moving.'
        : 'The draft has started, but you still need a team before you can participate.',
      actionLabel: teamId ? 'Join Live Draft' : 'Create Team',
      onClick: () => navigate(teamId ? buildDraftRoomPath(existingDraft._id, { chooseView: true }) : '/team'),
    });
  }

  if (existingDraft?.status === 'completed' && configurableBenchWeekNumber && teamId && isTeamManager && requiredBenchSlots > 0 && configurableBenchAthleteIds.length < requiredBenchSlots) {
    priorityCallouts.push({
      key: 'set-lineup',
      icon: <FaSatelliteDish />,
      tone: 'warning',
      title: `Set your lineup for Week ${configurableBenchWeekNumber}`,
      description: `Choose ${requiredBenchSlots} bench athlete${requiredBenchSlots === 1 ? '' : 's'} so your scoring lineup is ready before the week begins.`,
      actionLabel: 'Open Team Dashboard',
      onClick: () => navigate('/team'),
    });
  }

  if (existingDraft?.status === 'completed' && configurableBenchWeekNumber && teamId && !isTeamManager && requiredBenchSlots > 0 && configurableBenchAthleteIds.length < requiredBenchSlots) {
    priorityCallouts.push({
      key: 'waiting-on-lineup',
      icon: <FaClipboardList />,
      tone: 'info',
      title: `Week ${configurableBenchWeekNumber} lineup is not set yet`,
      description: 'Your team manager still needs to choose the bench group before the lineup is fully locked in.',
      actionLabel: 'View Team',
      onClick: () => navigate('/team'),
    });
  }

  const dismissLeagueManagerIntro = () => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(LEAGUE_MANAGER_SLIDESHOW_STORAGE_KEY, 'true');
    }
    setShowLeagueManagerIntro(false);
  };

  const openLeagueManagerSlideshow = () => {
    dismissLeagueManagerIntro();
    navigate(LEAGUE_MANAGER_SLIDESHOW_PATH);
  };

  return (
    <>
      <Modal
        show={showLeagueManagerIntro}
        onHide={dismissLeagueManagerIntro}
        centered
        contentClassName="border-0 overflow-hidden"
      >
        <Modal.Header closeButton style={{ backgroundColor: 'var(--bg-primary)', borderBottom: '1px solid var(--border-light)' }}>
          <Modal.Title>New to League Runner?</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: 'var(--bg-primary)', padding: '1rem' }}>
          <button
            type="button"
            onClick={openLeagueManagerSlideshow}
            className="w-100 text-start p-0 border-0 bg-transparent"
            aria-label="Open the League Runner slideshow for league managers"
          >
            <div className="modern-card mb-0" style={{ overflow: 'hidden', cursor: 'pointer' }}>
              <img
                src={LEAGUE_MANAGER_SLIDESHOW_THUMBNAIL}
                alt="Preview of the League Runner league manager slideshow"
                style={{ display: 'block', width: '100%', height: 'auto', borderBottom: '1px solid var(--border-light)' }}
              />
              <div className="p-3">
                <div className="feature-kicker">Create a League Tour</div>
                <h2 className="h4 mb-2" style={{ color: 'var(--text-primary)' }}>See the new four-step walkthrough for creating and setting up a league.</h2>
                <p className="text-secondary mb-0">Click this preview to open the full slideshow.</p>
              </div>
            </div>
          </button>
        </Modal.Body>
      </Modal>

      <div className="home-screen">
      <section className="full-width-hero">
        <div className="hero-shell">
          <div className="hero-banner">
            <div className="hero-content-panel fade-in">
              <div className="hero-kicker">The fantasy league where the players are the athletes</div>
              <h1 className="hero-title">You Are the Athlete</h1>
              <p className="hero-subtitle">
                Create or join a league, draft your team, and earn points for every mile you run.
              </p>
              {showHeroDraftDate && (
                <p className="hero-subtitle mb-0">
                  {showHeroEnterDraft ? 'Your league draft is live today.' : `Next draft: ${draftStartsAt}`}
                </p>
              )}
              <div className="hero-actions">
                {showHeroEnterDraft && (
                  <LinkContainer to={buildDraftRoomPath(existingDraft._id, { chooseView: true })}>
                    <button className="btn btn-primary-modern btn-lg-modern" type="button">
                      Enter Draft
                    </button>
                  </LinkContainer>
                )}
                {userInfo ? (
                  athlete?.team ? (
                    <LinkContainer to="/team/">
                      <button className="btn btn-primary-modern btn-lg-modern" type="button">
                        View Your Team
                      </button>
                    </LinkContainer>
                  ) : athlete?.league ? (
                    <LinkContainer to={leaguePath}>
                      <button className="btn btn-primary-modern btn-lg-modern" type="button">
                        Open League Hub
                      </button>
                    </LinkContainer>
                  ) : (
                    <LinkContainer to={leaguePath}>
                      <button className="btn btn-primary-modern btn-lg-modern" type="button">
                        Browse Leagues
                      </button>
                    </LinkContainer>
                  )
                ) : (
                  <LinkContainer to="/register">
                    <button className="btn btn-primary-modern btn-lg-modern" type="button">
                      Get Started
                    </button>
                  </LinkContainer>
                )}

                {showIntegrationButtons && (
                  <>
                    <button className="btn btn-secondary-modern btn-lg-modern" onClick={handleStravaAuth} type="button">
                      Connect Strava
                    </button>
                    <button className="btn btn-outline-modern btn-lg-modern" onClick={handleGarminAuth} type="button">
                      Connect Garmin
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {priorityCallouts.length > 0 && (
            <div className="home-priority-grid slide-up">
              {priorityCallouts.map((callout) => (
                <div className={`home-priority-card home-priority-card-${callout.tone}`} key={callout.key}>
                  <div className="home-priority-icon">{callout.icon}</div>
                  <div className="home-priority-copy">
                    <h3>{callout.title}</h3>
                    <p>{callout.description}</p>
                  </div>
                  <button className="btn btn-sm-modern btn-primary-modern" onClick={callout.onClick} type="button">
                    {callout.actionLabel}
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="hero-stat-grid slide-up">
            {stats.map((stat) => (
              <div className="hero-stat" key={stat.label}>
                <div className="hero-stat-icon">{stat.icon}</div>
                <span className="hero-stat-value">{stat.value}</span>
                <span className="hero-stat-label">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-modern">
        <div className="container-modern">
          <div className="feature-split">
            <div className="feature-media">
              <div className="feature-media-card">
                <strong>How League Runner works</strong>
                <span>Set up your league, draft your roster, and keep every athlete in sync.</span>
              </div>
            </div>

            <div className="feature-copy">
              <div className="feature-kicker">Gather your team!</div>
              <h2>Built for focused league management.</h2>
              <p>
                League Runner helps you manage your league efficiently, keeping all athletes and teams in sync throughout the season.
              </p>
              <div className="feature-list">
                {featureHighlights.map((item) => (
                  <div className="feature-item" key={item.label}>
                    <span className="feature-item-icon">{item.icon}</span>
                    <span>{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-modern soft-section">
        <div className="container-modern">
          <div className="home-section-heading">
            <div className="feature-kicker">Join the competition</div>
            <h2>Compete with your team and track your performance.</h2>
            <p>
              The more you run, the more points you earn for your team, helping you climb the league standings and track your performance over time.
            </p>
          </div>

          <div className="grid-modern grid-cols-2">
            <div className="modern-card slide-up">
              <div className="modern-card-header">
                <h3 className="modern-card-title">For the Stat Trackers</h3>
                <p className="modern-card-subtitle">Form teams and compete in organized leagues.</p>
              </div>
              <p>Love stats? Track your performance and see how you stack up against your teammates in the league standings.</p>
              <div className="mt-4">
                <LinkContainer to="/leagues/">
                  <button className="btn btn-outline-modern btn-sm-modern" type="button">
                    View Leagues
                  </button>
                </LinkContainer>
              </div>
            </div>

            <div className="modern-card slide-up">
              <div className="modern-card-header">
                <h3 className="modern-card-title">Performance Tracking</h3>
                <p className="modern-card-subtitle">Monitor mileage, activities, and points.</p>
              </div>
              <p>Connect your tracker, review recent activity, and compare your contribution inside the league.</p>
              {userInfo ? showIntegrationButtons ? (
                <div className="mt-4 d-flex flex-wrap gap-2">
                  <button className="btn btn-outline-modern btn-sm-modern" onClick={handleStravaAuth} type="button">
                    Connect Strava
                  </button>
                  <button className="btn btn-outline-modern btn-sm-modern" onClick={handleGarminAuth} type="button">
                    Connect Garmin
                  </button>
                </div>
              ) : (
                <div className="mt-4">
                  <LinkContainer to="/profile">
                    <button className="btn btn-outline-modern btn-sm-modern" type="button">
                      Manage Connections
                    </button>
                  </LinkContainer>
                </div>
              ) : (
                <div className="mt-4">
                  <LinkContainer to="/login">
                    <button className="btn btn-outline-modern btn-sm-modern" type="button">
                      Sign In to Connect
                    </button>
                  </LinkContainer>
                </div>
              )}
            </div>

            <div className="modern-card slide-up">
              <div className="modern-card-header">
                <h3 className="modern-card-title">Athlete Insights</h3>
                <p className="modern-card-subtitle">Check trends, goals, and season progress.</p>
              </div>
              <p>Use the athlete dashboard to see performance over time without the page feeling overloaded.</p>
              <div className="mt-4">
                <LinkContainer to="/dashboard">
                  <button className="btn btn-outline-modern btn-sm-modern" type="button">
                    My Dashboard
                  </button>
                </LinkContainer>
              </div>
            </div>

            <div className="modern-card slide-up">
              <div className="modern-card-header">
                <h3 className="modern-card-title">Community Momentum</h3>
                <p className="modern-card-subtitle">Running is more fun with your team and community.</p>
              </div>
              <p>Engage with your teammates, share your progress, and keep the community spirit alive throughout the season.</p>
              <div className="mt-4">
                <LinkContainer to="/register">
                  <button className="btn btn-outline-modern btn-sm-modern" type="button">
                    Sign Up
                  </button>
                </LinkContainer>
              </div>
            </div>
          </div>
        </div>
      </section>
      </div>
    </>
  );
};

export default HomeScreen;
